<style>
.menu {
  @apply fixed text-white shadow-md h-full overflow-x-auto top-0 left-0 transition-all duration-300 ease-in-out z-50;
  @apply border border-r border-tertiary-dark min-h-screen;
}
.background {
  @apply bg-secondary-light;
}
.dark .background {
  @apply bg-secondary-dark;
}
.item {
  @apply flex cursor-pointer items-center px-4 py-2 whitespace-nowrap;
}
.item:hover {
  color: #e63c24;
}
.item.active {
  color: #e63c24;
}
</style>

<script>
import { link } from "svelte-routing"
import { onMount } from "svelte"
import { slide } from "svelte/transition"
import { currentUser, deauth } from "@local/store/auth"
import Logo from "@local/components/images/Logo.svelte"
import Icon from "@local/components/images/Icon.svelte"
import {
  getDataFromLocalStorage,
  setDataToLocalStorage,
} from "@local/utils/localStorage"

let currentPath = window.location.pathname + window.location.search
let isSubMenuOpen = {}

const updateRouteState = () => {
  currentPath = window.location.pathname + window.location.search
  itemsSidebar.forEach((item, index) => {
    if (item.subitem?.some((sub) => sub.to === currentPath)) {
      isSubMenuOpen[index] = true
    }
  })
}

const navigate = (to) => {
  currentPath = to
}

onMount(() => {
  updateRouteState()
  window.addEventListener("popstate", updateRouteState)
  return () => window.removeEventListener("popstate", updateRouteState)
})

const itemsSidebar = [
  { to: "/", label: "Dashboard", icon: "queue_play_next" },
  { to: "/users", label: "Users", icon: "person", accessLevel: 3 },
  {
    to: "/workflows?type=medical",
    label: "Workflows",
    icon: "medical_information",
    accessLevel: 3,
  },
  {
    to: "/workflows?type=educator",
    label: "Educator",
    icon: "history_edu",
    accessLevel: 3,
  },
  {
    to: "#",
    label: "Who to call",
    icon: "call",
    accessLevel: 3,
    subitem: [
      { to: "/who-to-call", label: "Call List", icon: "rule", accessLevel: 3 },
      {
        to: "/who-to-call/users",
        label: "Config",
        icon: "visibility",
        accessLevel: 1,
      },
      {
        to: "/who-to-call/veterans",
        label: "Veterans",
        icon: "military_tech",
        accessLevel: 2,
      },
    ],
  },
  {
    to: "/calendar",
    label: "Calendar",
    icon: "calendar_month",
    accessLevel: 3,
  },
  {
    to: "#",
    label: "Data",
    icon: "query_stats",
    accessLevel: 1,
    subitem: [
      {
        to: "/reports/post-consults",
        label: "Post Consult",
        icon: "plagiarism",
        accessLevel: 1,
      },
      {
        to: "/reports/starseed",
        label: "Starseed",
        icon: "plagiarism",
        accessLevel: 1,
      },
      {
        to: "/reports/starseed-temp",
        label: "Starseed Temp",
        icon: "plagiarism",
        accessLevel: 1,
      },
      {
        to: "/reports/purchases",
        label: "Purchases",
        icon: "plagiarism",
        accessLevel: 1,
      },
      {
        to: "/reports/mdbilling",
        label: "MD Billing",
        icon: "plagiarism",
        accessLevel: 1,
      },
      {
        to: "/reports/text-consultation",
        label: "Text Consultation",
        icon: "plagiarism",
        accessLevel: 1,
      },
      {
        to: "/cancellation-list",
        label: "Cancellations",
        icon: "event_busy",
        accessLevel: 3,
      },
      {
        to: "/reports/phipa",
        label: "PHIPA",
        icon: "plagiarism",
        accessLevel: 1,
      },
    ],
  },
]

let userPreference = getDataFromLocalStorage("userPreference")
let darkMode = userPreference?.darkMode || false

const toggleSubMenu = (index) => {
  isSubMenuOpen[index] = !isSubMenuOpen[index]
}

function changeViewMode() {
  darkMode = !darkMode
  setDataToLocalStorage("userPreference", { darkMode })
  window.location.reload()
}
</script>

<nav
  class="menu w-24 md:w-52 transition-all duration-300 ease-in-out"
  class:dark="{darkMode}"
>
  <div class="bg-secondary-light dark:bg-secondary-dark min-h-screen">
    <div class="py-8 px-4">
      <a href="/" use:link on:click="{() => navigate('/')}">
        <div class="md:hidden"><Logo size="small" /></div>
        <div class="hidden md:block"><Logo size="regular" /></div>
      </a>
    </div>

    <div class="mt-4">
      {#each itemsSidebar as item, index}
        {#if item.accessLevel && $currentUser && $currentUser.roleLevel <= item.accessLevel}
          <div class="">
            {#if item.subitem?.length > 0}
              <button
                title="{item.label}"
                class="item justify-center md:justify-start w-full"
                on:click="{() => toggleSubMenu(index)}"
              >
                <Icon size="small">{item.icon}</Icon>
                <span class="ml-2 md:inline hidden">{item.label}</span>
                <Icon size="small" class="ml-2">
                  {isSubMenuOpen[index] ? "arrow_drop_up" : "arrow_drop_down"}
                </Icon>
              </button>

              {#if isSubMenuOpen[index]}
                <div class="pl-6" transition:slide="{{ duration: 300 }}">
                  {#each item.subitem as subitem}
                    <div class="py-2">
                      <a
                        title="{subitem.label}"
                        class="item"
                        class:active="{currentPath === subitem.to}"
                        href="{subitem.to}"
                        use:link
                        on:click="{() => navigate(subitem.to)}"
                      >
                        <Icon size="xl">{subitem.icon}</Icon>
                        <span class="ml-2 md:inline hidden"
                          >{subitem.label}</span
                        >
                      </a>
                    </div>
                  {/each}
                </div>
              {/if}
            {:else}
              <a
                title="{item.label}"
                class="item justify-center md:justify-start w-full"
                class:active="{currentPath === item.to}"
                href="{item.to}"
                use:link
                on:click="{() => navigate(item.to)}"
              >
                <Icon size="xl">{item.icon}</Icon>
                <span class="ml-2 md:inline hidden">{item.label}</span>
              </a>
            {/if}
          </div>
          <hr
            class="border-t border-black border-gray-400 my-4 md:w-16 md:mx-6 w-8 mx-auto"
          />
        {/if}
      {/each}
      <div class="pt-24" on:click="{changeViewMode}">
        <button class="item justify-center md:justify-start w-full">
          {#if darkMode}
            <Icon size="xl">light_mode</Icon>
            <span class="ml-2 md:inline hidden">Light Mode</span>
          {:else}
            <Icon size="xl">dark_mode</Icon>
            <span class="ml-2 md:inline hidden">Dark Mode</span>
          {/if}
        </button>
      </div>

      <div title="Logout" class="py-4" on:click="{deauth}">
        <button class="item justify-center md:justify-start w-full">
          <Icon size="xl">logout</Icon>
          <span class="ml-2 md:inline hidden">Logout</span>
        </button>
      </div>
    </div>
  </div>
</nav>
